import {
  CollectionPreferencesProps,
  DateRangePickerProps,
  TableProps,
} from '@amzn/awsui-components-react-v3';
import _ from 'lodash';
import i18n from '../../../../i18n';
import { renderDateField, renderExceptionLink, renderField, renderList } from '../helper';
import { ReceiveExceptionSearchResult } from '../types';

export const columnDefinitions = [
  {
    id: 'id',
    header: i18n.t('Exception ID'),
    cell: renderExceptionLink,
  },
  {
    id: 'title',
    header: i18n.t('Title'),
    cell: renderField('title'),
  },
  {
    id: 'exceptionType',
    header: i18n.t('Exception Type'),
    cell: renderField('exceptionType'),
    sortingField: 'exceptionType.keyword',
  },
  {
    id: 'status',
    header: i18n.t('Status'),
    cell: renderField('status'),
    sortingField: 'status.keyword',
  },
  {
    id: 'site',
    header: i18n.t('Site'),
    cell: renderField('site'),
    sortingField: 'properties.site.keyword',
  },
  {
    id: 'model',
    header: i18n.t('Model'),
    cell: renderField('model'),
    sortingField: 'items.properties.model.keyword',
  },
  {
    id: 'createdBy',
    header: i18n.t('Created by'),
    cell: renderField('createdBy'),
  },
  {
    id: 'createdAt',
    header: i18n.t('Created at'),
    cell: renderDateField('createdAt'),
    sortingField: 'createdAt',
  },
  {
    id: 'assignedToId',
    header: i18n.t('Assigned To'),
    cell: renderField('assignedToId'),
  },
  {
    id: 'shipmentReferenceId',
    header: i18n.t('Shipment ID'),
    cell: renderField('shipmentReferenceId'),
    sortingField: 'shipmentReferenceId.keyword',
  },
  {
    id: 'orderNumber',
    header: i18n.t('Order Number'),
    cell: renderField('orderNumber'),
    sortingField: 'orderNumber.keyword',
  },
  {
    id: 'orderType',
    header: i18n.t('Order Type'),
    cell: renderField('orderType'),
    sortingField: 'orderType.keyword',
  },
  {
    id: 'orderLineNumber',
    header: i18n.t('Order Line Number'),
    cell: renderField('orderLineNumber'),
    sortingField: 'orderLineNumber.keyword',
  },
  {
    id: 'trackingNumber',
    header: i18n.t('Tracking or Bill of Lading (BOL) Number'),
    cell: renderField('trackingNumber'),
    sortingField: 'trackingNumber.keyword',
  },
  {
    id: 'holdingLocation',
    header: i18n.t('Holding Container'),
    cell: renderField('holdingLocation'),
    sortingField: 'holdingLocation.keyword',
  },
  {
    id: 'legacyModelId',
    header: i18n.t('APN or IPN'),
    cell: renderField('legacyModelId'),
    sortingField: 'legacyModelId.keyword',
  },
  {
    id: 'quantity',
    header: i18n.t('Quantity'),
    cell: renderField('quantity'),
    sortingField: 'quantity',
  },
  {
    id: 'serials',
    header: i18n.t('Serial Numbers'),
    cell: renderList('serials'),
  },
];

export const DEFAULT_VISIBLE_COLUMNS = [
  'id',
  'title',
  'exceptionType',
  'status',
  'site',
  'model',
  'createdBy',
  'createdAt',
  'assignedToId',
  'shipmentReferenceId',
  'orderNumber',
  'orderType',
  'orderLineNumber',
  'trackingNumber',
  'holdingLocation',
  'legacyModelId',
  'quantity',
  'serials',
];

export const DEFAULT_PAGE_SIZE = 20;

export const DEFAULT_SORT_BY = 'createdAt';

export const DEFAULT_SORT_ORDER = 'DESC';

export const PAGE_SIZE_PREFERENCES = {
  title: i18n.t('Select page size'),
  options: [
    { value: 10, label: `10 ${i18n.t('exceptions')}` },
    { value: 20, label: `20 ${i18n.t('exceptions')}` },
  ],
};

export const VISIBLE_COLUMNS_PREFERENCES: CollectionPreferencesProps.VisibleContentPreference = {
  title: i18n.t('Select visible columns'),
  options: [
    {
      label: i18n.t('Receive Exception properties'),
      options: DEFAULT_VISIBLE_COLUMNS.map((id) => {
        return columnDefinitions.find(
          (definition) => _.get(definition, 'id') === id
        ) as TableProps.ColumnDefinition<ReceiveExceptionSearchResult>;
      }).map((entry) => {
        return { id: entry?.id, label: entry?.header };
      }) as CollectionPreferencesProps.VisibleContentOption[],
    },
  ],
};

export const DATE_RANGE_PICKER_RELATIVE_OPTIONS: DateRangePickerProps.RelativeOption[] = [
  {
    key: 'previous-month',
    amount: 30,
    unit: 'day',
    type: 'relative',
  },
  {
    key: 'previous-week',
    amount: 7,
    unit: 'day',
    type: 'relative',
  },
  {
    key: '24-hours',
    amount: 24,
    unit: 'hour',
    type: 'relative',
  },
];

export const DEFAULT_SEARCH_FILTERS = {
  assignedToId: '',
  createdBy: '',
  createdAt: null,
  exceptionType: null,
  title: '',
  model: '',
  reference: '',
  site: '',
  status: null,
};

export const DATE_RANGE_PICKER_LABELS = {
  todayAriaLabel: i18n.t('Today'),
  nextMonthAriaLabel: i18n.t('Next month'),
  previousMonthAriaLabel: i18n.t('Previous month'),
  customRelativeRangeDurationLabel: i18n.t('Duration'),
  customRelativeRangeDurationPlaceholder: i18n.t('Enter duration'),
  customRelativeRangeOptionLabel: i18n.t('Custom range'),
  customRelativeRangeOptionDescription: i18n.t('Set a custom range in the past'),
  customRelativeRangeUnitLabel: i18n.t('Unit of time'),
  formatRelativeRange: (e: { amount: number; unit: DateRangePickerProps.TimeUnit }): string => {
    const t = e.amount === 1 ? e.unit : `${e.unit}s`;
    return `Last ${e.amount} ${t}`;
  },
  formatUnit: (e: string, t: number): string => (t === 1 ? e : `${e}s`),
  dateTimeConstraintText: i18n.t('Range must be between 6 - 30 days. Use 24 hour format.'),
  relativeModeTitle: i18n.t('Relative range'),
  absoluteModeTitle: i18n.t('Absolute range'),
  relativeRangeSelectionHeading: i18n.t('Choose a range'),
  startDateLabel: i18n.t('Start date'),
  endDateLabel: i18n.t('End date'),
  startTimeLabel: i18n.t('Start time'),
  endTimeLabel: i18n.t('End time'),
  clearButtonLabel: i18n.t('Clear'),
  cancelButtonLabel: i18n.t('Cancel'),
  applyButtonLabel: i18n.t('Apply'),
};
